import { BenefitType } from "./claims/disability/BenefitType";
import { findMapKeyByValue } from "./FindKeyByValue";
import { Path } from "./Path";
import {
    accidentBenefitQueryPart,
    criticalIllnessBenefitQueryPart,
    hospitalIndemnityBenefitQueryPart,
    lifeBenefitQueryPart,
    longTermDisabilityBenefitQueryPart,
    paidFamilyLeaveBenefitQueryPart,
    paidMedicalLeaveBenefitQueryPart, paidParentalLeaveBenefitQueryPart,
    shortTermDisabilityBenefitQueryPart,
    voluntaryTermLifeBenefitQueryPart
} from "../atoms/Queries";
import { Member } from "../domain/Member";
import { State } from "./State";
import { SourceSystem } from "./claims/disability/SourceSystem";
import {
    AccidentalDeathAndDismembermentPolicyCoverage,
    AccidentPolicyCoverage,
    CriticalIllnessPolicyCoverage, GroupTermLifePolicyCoverage, HospitalIndemnityMaternity,
    HospitalIndemnityPolicyCoveragePlan, MaternityEnum,
    PolicyCoverage, PortabilityEnum, ShortTermDisabilityPolicyCoverage,
    WellnessBenefitAmountEnum
} from "../generated/graphql";
import _ from "lodash";
import {EmployeeAssistanceProgramProvision} from "./EmployeeAssistanceProgramProvision";
import {DateTime} from "luxon";
import is2026OrAfter = EmployeeAssistanceProgramProvision.is2026OrAfter;

export enum Coverage {
    ACCIDENT = 'accident',
    CRITICAL_ILLNESS = 'criticalIllness',
    HOSPITAL_INDEMNITY = 'hospitalIndemnity',
    LIFE = 'life',
    LONG_TERM_DISABILITY = 'longTerm',
    PAID_FAMILY_LEAVE = 'paidFamily',
    PAID_MEDICAL_LEAVE = 'paidMedical',
    PAID_PARENTAL_LEAVE = 'paidParental',
    SHORT_TERM_DISABILITY = 'shortTerm',
    VOLUNTARY_TERM_LIFE = 'voluntaryLife',
    WELLNESS = 'wellness'
}

const namesByCoverage = new Map([
    [Coverage.ACCIDENT, 'Accident'],
    [Coverage.CRITICAL_ILLNESS, 'Critical illness'],
    [`${Coverage.CRITICAL_ILLNESS}_NY`, 'Specified disease'],
    [Coverage.HOSPITAL_INDEMNITY, 'Hospital indemnity'],
    [Coverage.LIFE, 'Life'],
    [Coverage.LONG_TERM_DISABILITY, 'Long-term disability'],
    [Coverage.PAID_FAMILY_LEAVE, 'Paid family leave'],
    [Coverage.PAID_MEDICAL_LEAVE, 'Paid medical leave'],
    [Coverage.PAID_PARENTAL_LEAVE, 'Paid parental leave'],
    [Coverage.SHORT_TERM_DISABILITY, 'Short-term disability'],
    [Coverage.VOLUNTARY_TERM_LIFE, 'Voluntary term life'],
    [Coverage.WELLNESS, 'Wellness'],
])

const benefitTypesByCoverage = new Map([
    [Coverage.ACCIDENT, [BenefitType.ACCIDENT, BenefitType.ACCIDENT_WELLNESS]],
    [Coverage.CRITICAL_ILLNESS, [BenefitType.CRITICAL_ILLNESS, BenefitType.CRITICAL_ILLNESS_WELLNESS]],
    [Coverage.HOSPITAL_INDEMNITY, [BenefitType.HOSPITAL_INDEMNITY, BenefitType.HOSPITAL_INDEMNITY_WELLNESS]],
    [Coverage.LIFE, [BenefitType.LIFE, BenefitType.LIFE_WAIVER]],
    [Coverage.LONG_TERM_DISABILITY, [BenefitType.LONG_TERM_DISABILITY]],
    [Coverage.PAID_FAMILY_LEAVE, [BenefitType.PAID_FAMILY_LEAVE]],
    [Coverage.PAID_MEDICAL_LEAVE, [BenefitType.PAID_MEDICAL_LEAVE]],
    [Coverage.PAID_PARENTAL_LEAVE, [BenefitType.PAID_PARENTAL_LEAVE]],
    [Coverage.SHORT_TERM_DISABILITY, [BenefitType.SHORT_TERM_DISABILITY]],
    [Coverage.VOLUNTARY_TERM_LIFE, [BenefitType.LIFE, BenefitType.LIFE_WAIVER]],
    [Coverage.WELLNESS, [BenefitType.ACCIDENT_WELLNESS, BenefitType.CRITICAL_ILLNESS_WELLNESS, BenefitType.HOSPITAL_INDEMNITY_WELLNESS]]
])

const contentNavigatorNamesByCoverage = new Map([
    [Coverage.ACCIDENT, 'ACCIDENT'],
    [Coverage.CRITICAL_ILLNESS, 'CRITICAL ILLNESS'],
    [Coverage.HOSPITAL_INDEMNITY, 'HOSPITAL INDEMNITY'],
    [Coverage.LIFE, 'LIFE'],
    [Coverage.LONG_TERM_DISABILITY, 'LONG TERM DISABILITY'],
    [Coverage.PAID_FAMILY_LEAVE, 'PAID FAMILY AND MEDICAL LEAVE'],
    [Coverage.PAID_MEDICAL_LEAVE, 'PAID FAMILY AND MEDICAL LEAVE'],
    [Coverage.PAID_PARENTAL_LEAVE, 'PAID FAMILY AND MEDICAL LEAVE'],
    [Coverage.SHORT_TERM_DISABILITY, 'SHORT TERM DISABILITY'],
    [Coverage.VOLUNTARY_TERM_LIFE, 'VOLUNTARY TERM LIFE'],
    [`${Coverage.VOLUNTARY_TERM_LIFE}_PORT`, 'PORT VTL']
])

const paperFormsByCoverage = new Map([
    [Coverage.ACCIDENT, 'CBFCIBAA3AAABLblqZhDllwT7GkTrYHTXMCWn6455LOLuMZn-wsoJe9jxtY8fz8EodKo52AQYzxqx4CwK4nI*'],
    [Coverage.CRITICAL_ILLNESS, 'CBFCIBAA3AAABLblqZhCzUGoURH9SSNQqrMAqKzj_O-%20FQcHkmRIMV8fYEiF5Aa8e4k9SLuqz_W2VJf2QbRiw*'],
    [`${Coverage.CRITICAL_ILLNESS}_NY`, 'CBFCIBAA3AAABLblqZhAu87Jv-o771vxnLPKsn5e3utUblXQFxFrA3b-1U_iSbvHJ7HPZJB66im_OuNoGSWs*'],
    [Coverage.HOSPITAL_INDEMNITY, 'CBFCIBAA3AAABLblqZhB_G6JrGolP_cmBssJJWwduQpZJTAYjgfg41UPkzNT9qakQpmt4B_sn6T56Jlc0VGE*'],
    [Coverage.LIFE, 'CBFCIBAA3AAABLblqZhCf576_9p10JW3ThI1aBIXnULKRbN1uEw_S1_NCaxgFBg79J9NvG48kwn5dE_q1pkQ*'],
    [Coverage.LONG_TERM_DISABILITY, 'CBFCIBAA3AAABLblqZhBYQTyK-JEpvn-r04sI8X9U6PZbBXmceSSqgDL1F9rbLehW51wblNIVJoIFjYn_4bg*'],
    [Coverage.PAID_FAMILY_LEAVE, ''],
    [Coverage.PAID_MEDICAL_LEAVE, ''],
    [Coverage.PAID_PARENTAL_LEAVE, ''],
    [Coverage.SHORT_TERM_DISABILITY, 'CBFCIBAA3AAABLblqZhBYQTyK-JEpvn-r04sI8X9U6PZbBXmceSSqgDL1F9rbLehW51wblNIVJoIFjYn_4bg*'],
    [Coverage.VOLUNTARY_TERM_LIFE, 'CBFCIBAA3AAABLblqZhCf576_9p10JW3ThI1aBIXnULKRbN1uEw_S1_NCaxgFBg79J9NvG48kwn5dE_q1pkQ*'],
    [Coverage.WELLNESS, 'CBFCIBAA3AAABLblqZhAeNJVViS4QZJXM5GB6UrusNIUEcH__d_eofgaIh8WKPl60gUb3pjfEvtwW7CIUr28*'],
])

const physicianStatementFormsByCoverage = new Map([
    [Coverage.ACCIDENT, 'CBFCIBAA3AAABLblqZhDllwT7GkTrYHTXMCWn6455LOLuMZn-wsoJe9jxtY8fz8EodKo52AQYzxqx4CwK4nI*'],
    [Coverage.CRITICAL_ILLNESS, 'CBFCIBAA3AAABLblqZhCzUGoURH9SSNQqrMAqKzj_O-%20FQcHkmRIMV8fYEiF5Aa8e4k9SLuqz_W2VJf2QbRiw*'],
    [`${Coverage.CRITICAL_ILLNESS}_NY`, 'CBFCIBAA3AAABLblqZhAu87Jv-o771vxnLPKsn5e3utUblXQFxFrA3b-1U_iSbvHJ7HPZJB66im_OuNoGSWs*'],
    [Coverage.HOSPITAL_INDEMNITY, ''],
    [Coverage.LIFE, ''],
    [Coverage.LONG_TERM_DISABILITY, 'CBFCIBAA3AAABLblqZhDdIwS4vOpL5CC_xjPGBvhrSqoPJS_cmYft6j58ZTk0EUpcXWs_VUqjawtQzz3lV-g*'],
    [Coverage.PAID_FAMILY_LEAVE, ''],
    [Coverage.PAID_MEDICAL_LEAVE, ''],
    [Coverage.PAID_PARENTAL_LEAVE, ''],
    [Coverage.SHORT_TERM_DISABILITY, 'CBFCIBAA3AAABLblqZhDdIwS4vOpL5CC_xjPGBvhrSqoPJS_cmYft6j58ZTk0EUpcXWs_VUqjawtQzz3lV-g*'],
    [Coverage.VOLUNTARY_TERM_LIFE, ''],
    [Coverage.WELLNESS, ''],
])

const queryPartsByCoverage = new Map([
    [Coverage.ACCIDENT, accidentBenefitQueryPart],
    [Coverage.CRITICAL_ILLNESS, criticalIllnessBenefitQueryPart],
    [Coverage.HOSPITAL_INDEMNITY, hospitalIndemnityBenefitQueryPart],
    [Coverage.LIFE, lifeBenefitQueryPart],
    [Coverage.LONG_TERM_DISABILITY, longTermDisabilityBenefitQueryPart],
    [Coverage.PAID_FAMILY_LEAVE, paidFamilyLeaveBenefitQueryPart],
    [Coverage.PAID_MEDICAL_LEAVE, paidMedicalLeaveBenefitQueryPart],
    [Coverage.PAID_PARENTAL_LEAVE, paidParentalLeaveBenefitQueryPart],
    [Coverage.SHORT_TERM_DISABILITY, shortTermDisabilityBenefitQueryPart],
    [Coverage.VOLUNTARY_TERM_LIFE, voluntaryTermLifeBenefitQueryPart]
])

const portabilityRatesByCoverage = new Map([
    [Coverage.ACCIDENT, 'GP62260'],
    [Coverage.CRITICAL_ILLNESS, 'GP61077WOC'],
    [`${Coverage.CRITICAL_ILLNESS}_WC`, 'GP61077WC'],
    [Coverage.HOSPITAL_INDEMNITY, null],
    [Coverage.LIFE, null],
    [Coverage.LONG_TERM_DISABILITY, null],
    [Coverage.PAID_FAMILY_LEAVE, null],
    [Coverage.PAID_MEDICAL_LEAVE, null],
    [Coverage.PAID_PARENTAL_LEAVE, null],
    [Coverage.SHORT_TERM_DISABILITY, null],
    [Coverage.VOLUNTARY_TERM_LIFE, null],
])

const claimFormPathsByCoverage = new Map([
    [Coverage.ACCIDENT, 'accident/employee'],
    [Coverage.CRITICAL_ILLNESS, 'criticalIllness/employee'],
    [Coverage.HOSPITAL_INDEMNITY, 'hospitalIndemnity/employee'],
    [Coverage.LIFE, 'life/beneficiary'],
    [Coverage.LONG_TERM_DISABILITY, 'disability/employee'],
    [Coverage.PAID_FAMILY_LEAVE, 'pfml/employee'],
    [Coverage.PAID_MEDICAL_LEAVE, 'pfml/employee'],
    [Coverage.PAID_PARENTAL_LEAVE, 'pfml/employee'],
    [Coverage.SHORT_TERM_DISABILITY, 'disability/employee'],
    [Coverage.VOLUNTARY_TERM_LIFE, 'life/beneficiary'],
    [Coverage.WELLNESS, 'wellness/employee'],
])

const benefitServiceNameByCoverage = new Map([
    [Coverage.ACCIDENT, 'accident'],
    [Coverage.CRITICAL_ILLNESS, 'criticalIllness'],
    [Coverage.HOSPITAL_INDEMNITY, 'hospitalIndemnityChoice'],
    [Coverage.LIFE, 'groupTermLife'],
    [Coverage.LONG_TERM_DISABILITY, 'longTermDisability'],
    [Coverage.PAID_FAMILY_LEAVE, 'paidFamilyLeave'],
    [Coverage.PAID_MEDICAL_LEAVE, 'paidMedicalLeave'],
    [Coverage.PAID_PARENTAL_LEAVE, 'paidParentalLeave'],
    [Coverage.SHORT_TERM_DISABILITY, 'shortTermDisability'],
    [Coverage.VOLUNTARY_TERM_LIFE, 'voluntaryTermLife']
])

export namespace Coverage {
    export function findCoverageNameFor(coverage: Coverage, member: Member): string {
        const nameKey = coverage + findStateSuffixFor(member, coverage);
        return namesByCoverage.get(nameKey) as string;
    }

    export function findOnlineClaimFormUrlFor(coverage: Coverage): string {
        return `${process.env.REACT_APP_DISABILITY_CLAIM_FORMS_URL}/${claimFormPathsByCoverage.get(coverage)}`
    }

    export function findPaperClaimFormNumberFor(coverage: Coverage, member: Member): string {
        const formKey = coverage + findStateSuffixFor(member, coverage);
        return paperFormsByCoverage.get(formKey) as string;
    }

    export function findPhysicianStatementFormNumberFor(coverage: Coverage, member: Member): string {
        const formKey = coverage + findStateSuffixFor(member, coverage);
        return physicianStatementFormsByCoverage.get(formKey) as string;
    }

    export function findCoverageUrlFor(coverage: Coverage, path: Path): string {
        return `/${coverage}/${path}`
    }

    export function findBenefitTypesFor(coverage: Coverage): BenefitType[] {
        return benefitTypesByCoverage.get(coverage) as BenefitType[];
    }

    export function findContentNavigatorNameFor(coverage: Coverage, isPortability: boolean): string {
        const portabilitySuffix = Coverage.VOLUNTARY_TERM_LIFE === coverage && isPortability ? '_PORT' : '';
        const contentNavigatorKey = coverage + portabilitySuffix;
        return contentNavigatorNamesByCoverage.get(contentNavigatorKey) as string;
    }

    export function findBenefitServiceQueryPartFor(coverage: Coverage): string {
        return queryPartsByCoverage.get(coverage) as string;
    }

    export function findPortabilityRatesFormNumberFor(coverage: Coverage, product: PolicyCoverage): string {
        const cancerSuffix = (product as CriticalIllnessPolicyCoverage)?.configuration?.cancerOneTwo ? '_WC' : '';
        const formKey = coverage + cancerSuffix;
        return portabilityRatesByCoverage.get(formKey) as string;
    }

    export function hasWellness(coverage: Coverage, product: PolicyCoverage): boolean {
        return ([Coverage.ACCIDENT, Coverage.CRITICAL_ILLNESS].includes(coverage)
                && ![undefined, null, WellnessBenefitAmountEnum.None, WellnessBenefitAmountEnum.NotApplicable].includes((product as AccidentPolicyCoverage)?.configuration?.wellnessBenefitAmount))
            || (Coverage.HOSPITAL_INDEMNITY === coverage
                && !!(product as HospitalIndemnityPolicyCoveragePlan)?.configuration?.provisions?.healthScreening?.included)
    }

    export function hasPortability(coverage: Coverage, product: PolicyCoverage): boolean {
        return ([Coverage.ACCIDENT, Coverage.CRITICAL_ILLNESS, Coverage.VOLUNTARY_TERM_LIFE, Coverage.HOSPITAL_INDEMNITY].includes(coverage)
                && ![undefined, null, PortabilityEnum.None].includes((product as AccidentPolicyCoverage)?.configuration?.portability))
    }

    export function hasPreExistingLimitation(coverage: Coverage, product: PolicyCoverage): boolean {
        return Coverage.CRITICAL_ILLNESS === coverage
            || ([Coverage.SHORT_TERM_DISABILITY, Coverage.LONG_TERM_DISABILITY, Coverage.HOSPITAL_INDEMNITY].includes(coverage)
                && !!(product as ShortTermDisabilityPolicyCoverage)?.configuration?.preExistingConditions?.included)
    }

    export function hasMaternityLimitation(coverage: Coverage, product: PolicyCoverage): boolean {
        return (Coverage.HOSPITAL_INDEMNITY === coverage
                && HospitalIndemnityMaternity.FullMaternity !== (product as HospitalIndemnityPolicyCoveragePlan)?.configuration?.provisions?.maternity)
            || (Coverage.SHORT_TERM_DISABILITY === coverage
                && MaternityEnum.FullMaternity !== (product as ShortTermDisabilityPolicyCoverage)?.configuration?.maternity)
    }

    export function hasBeneficiaries(coverage: Coverage, product: PolicyCoverage) {
        return (Coverage.ACCIDENT === coverage && (product as AccidentPolicyCoverage)?.configuration?.includesAccidentalDeathAndDismemberment)
            || [Coverage.LIFE, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage);
    }

    export function hasResources(coverage: Coverage) {
        return [Coverage.LIFE, Coverage.LONG_TERM_DISABILITY, Coverage.SHORT_TERM_DISABILITY, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage);
    }

    export function hasBooklet(coverage: Coverage) {
        return !isPaidLeave(coverage);
    }

    export function hasScheduleOfBenefits(coverage: Coverage) {
        return [Coverage.LONG_TERM_DISABILITY, Coverage.SHORT_TERM_DISABILITY, Coverage.CRITICAL_ILLNESS, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage);
    }

    export function hasBenefitsFlyer(coverage: Coverage) {
        return isPaidLeave(coverage);
    }

    export function hasUnderwriting(coverage: Coverage) {
        return [Coverage.CRITICAL_ILLNESS, Coverage.LIFE, Coverage.LONG_TERM_DISABILITY, Coverage.SHORT_TERM_DISABILITY, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage);
    }

    export function findBenefitsFlyerFormNumberFor(coverage: Coverage, workState: string) {
        if(!isPaidLeave(coverage)) {
            return null;
        }
        return Coverage.PAID_PARENTAL_LEAVE === coverage ? `GP63268${workState}` : `GP62729${workState}`;
    }

    export function findBenefitServiceNameFor(coverage: Coverage): string {
        return benefitServiceNameByCoverage.get(coverage) as string
    }

    export function findByBenefitServiceName(benefitServiceName: string): Coverage {
        return findMapKeyByValue(benefitServiceNameByCoverage, benefitServiceName) as Coverage;
    }

    export function hasLifeAccidentalDeathAndDismemberment(coverage: Coverage, product: PolicyCoverage): boolean {
        return [Coverage.LIFE, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage) &&
            ['accidentalDeathAndDismemberment', 'spouse.accidentalDeathAndDismemberment']
            .map(accidentalDeathBenefitProperty => _.get(product, accidentalDeathBenefitProperty) as AccidentalDeathAndDismembermentPolicyCoverage)
            ?.some(accidentalDeathBenefitProperty => accidentalDeathBenefitProperty?.isStandardIncluded)
    }

    export function hasAcceleratedBenefit(coverage: Coverage, product: PolicyCoverage): boolean {
        return [Coverage.LIFE, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage) &&
            !!(product as GroupTermLifePolicyCoverage)?.configuration?.acceleratedBenefits
    }

    export function hasElectronicFundsTransfer(coverage: Coverage): boolean {
        return ![Coverage.LIFE, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage);
    }

    export function findSourceSystemsFor(coverage: Coverage): SourceSystem[] {
        if(isPaidLeave(coverage)) {
            return [SourceSystem.FINEOS_CLOUD]
        } else if(isLife(coverage) || isDisability(coverage)) {
            return [SourceSystem.CMS, SourceSystem.FCS, SourceSystem.CLAIMS_SERVICE]
        } else if(Coverage.ACCIDENT === coverage) {
            return [SourceSystem.FCS, SourceSystem.CLAIMS_SERVICE]
        } else if(Coverage.CRITICAL_ILLNESS === coverage) {
            return [SourceSystem.CMS, SourceSystem.CLAIMS_SERVICE]
        } else if(Coverage.HOSPITAL_INDEMNITY === coverage) {
            return [SourceSystem.CLAIMS_SERVICE]
        }
        return []
    }

    export function hasEmployeeAssistanceProgram(coverage: Coverage, currentTime: DateTime): boolean {
        return isDisability(coverage) || (isLife(coverage) && is2026OrAfter(currentTime));
    }

    export function hasPhysicianStatement(coverage: Coverage): boolean {
        return isDisability(coverage) || [Coverage.ACCIDENT, Coverage.CRITICAL_ILLNESS].includes(coverage);
    }

    export function hasTaxWithholding(coverage: Coverage): boolean {
        return isDisability(coverage);
    }

    export function hasSupplementalInformation(coverage: Coverage): boolean {
        return Coverage.LONG_TERM_DISABILITY === coverage;
    }

    export function hasSocialSecurity(coverage: Coverage): boolean {
        return Coverage.LONG_TERM_DISABILITY === coverage;
    }

    export function hasPregnancyClaimForm(coverage: Coverage): boolean {
        return Coverage.SHORT_TERM_DISABILITY === coverage;
    }

    export function hasTravelAssistance(coverage: Coverage): boolean {
        return Coverage.LIFE === coverage;
    }

    export function hasWillAndLegalDocumentCenter(coverage: Coverage): boolean {
        return isLife(coverage);
    }

    export function hasEmpathy(coverage: Coverage): boolean {
        return isLife(coverage);
    }

    export function hasOpenEnrollmentBenefitIncrease(coverage: Coverage): boolean {
        return Coverage.VOLUNTARY_TERM_LIFE === coverage;
    }

    export function showBenefitsForSelfAccounting(coverage: Coverage): boolean {
        return isPaidLeave(coverage);
    }

    export function isPaidLeave(coverage: Coverage) {
        return [Coverage.PAID_FAMILY_LEAVE, Coverage.PAID_MEDICAL_LEAVE, Coverage.PAID_PARENTAL_LEAVE].includes(coverage);
    }

    export function findDepartmentExtensionFor(coverage: Coverage) {
        return isPaidLeave(coverage) ? '62477' : '';
    }

    function isDisability(coverage: Coverage) {
        return [Coverage.LONG_TERM_DISABILITY, Coverage.SHORT_TERM_DISABILITY].includes(coverage);
    }

    function isLife(coverage: Coverage) {
        return [Coverage.LIFE, Coverage.VOLUNTARY_TERM_LIFE].includes(coverage);
    }

    function findStateSuffixFor(member: Member, coverage: Coverage) {
        return member?.benefit?.contractState === State.NY && Coverage.CRITICAL_ILLNESS === coverage ? '_NY' : '';
    }
}